import {useQueryClient} from '@tanstack/react-query';
import {SortingState} from '@tanstack/table-core';
import React, {useCallback} from 'react';
import animateCardToTarget from '@/utils/animateCardToTarget';
import {cleanupMarkedContentSubmissions} from '@/utils/cache-updaters/contentSubmissions';
import {Phase} from '@/config/contentSubmissionPhases';
import {ContentSubmission} from '@/types/contentSubmission';
import EmptyState from '@/views/campaigns/components/content-submissions/empty-state';
import getEmptyStateContent from '@/views/campaigns/components/content-submissions/empty-state/getEmptyStateContent';
import {ContentSubmissionAction} from '@/components/content-submission-card';
import InfiniteLoader from '@/components/infinite-loader';
import Table from '@/components/table';
import {CAMPAIGN_CONTENT_SUBMISSIONS_QUERY_KEY} from '@/hooks/queries/use-campaign-content-submissions-query';
import useContentSubmissionAction from '@/hooks/use-content-submission-action';
import useNavigation from '@/hooks/use-navigation';
import styles from './ContentSubmissionsTable.module.scss';
import useContentSubmissionsTableColumns from './hooks/use-content-submissions-table-columns';

type ContentSubmissionsTableProps = {
  campaignId: string;
  contentSubmissions: ContentSubmission[];
  campaignNetwork?: string | null;
  campaignState?: string | null;
  selectedPhase?: Phase;
  isAdmin?: boolean;
  getCardLink: (cs: ContentSubmission) => string;
  activeFiltersKey?: string;
  onEndReached?: () => void;
  isLoadingInitial?: boolean;
  isLoadingMore?: boolean;
  isOffPlatform?: boolean;
  onTransitionSuccess?: (contentSubmissionId: string) => void;
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
  screenName?: string;
  onRowClick?: (context: {creator_id?: string; flow_id: string}) => void;
  onRowMenuOpen?: (context: {creator_id?: string; flow_id: string}) => void;
  onRowActionSelected?: (
    action: ContentSubmissionAction,
    context: {creator_id?: string; flow_id: string}
  ) => void;
};

function ContentSubmissionsTable(props: ContentSubmissionsTableProps) {
  const {
    campaignId,
    contentSubmissions,
    campaignNetwork,
    campaignState,
    selectedPhase,
    isAdmin,
    getCardLink,
    activeFiltersKey,
    onEndReached,
    isLoadingInitial,
    isLoadingMore,
    isOffPlatform = false,
    onTransitionSuccess,
    sorting = [],
    onSortingChange,
    screenName,
    onRowClick,
    onRowMenuOpen,
    onRowActionSelected,
  } = props;
  const queryClient = useQueryClient();
  const {navigate} = useNavigation();

  const onBeforeTransition = useCallback(
    async (id: string, targetPhase: Phase) => {
      const rowEl = document.querySelector<HTMLElement>(`tr[id="${id}"]`);
      const sidebarEl = document.querySelector<HTMLElement>(
        `[data-testid="sidebar-item-${targetPhase}"]`
      );
      if (rowEl && sidebarEl) {
        await animateCardToTarget(rowEl, sidebarEl);
      }
    },
    []
  );

  const {handleAction, actionComponent} = useContentSubmissionAction({
    campaignId,
    contentSubmissions,
    isOffPlatform,
    screenName,
    onBeforeTransition,
    onSuccess: onTransitionSuccess,
  });

  const columns = useContentSubmissionsTableColumns({
    campaignId,
    campaignNetwork,
    campaignState,
    selectedPhase,
    isAdmin,
    isOffPlatform,
    onAction: handleAction,
    onRowMenuOpen,
    onRowActionSelected,
  });

  const handleRowClick = useCallback(
    (row: ContentSubmission) => {
      onRowClick?.({creator_id: row.profile?.id, flow_id: row.id});
      navigate(getCardLink(row));
    },
    [navigate, getCardLink, onRowClick]
  );

  // Cleanup transitioned items when filters/phase changes or on unmount.
  React.useEffect(() => {
    if (!activeFiltersKey) return undefined;
    return () => {
      cleanupMarkedContentSubmissions(campaignId);
      queryClient.invalidateQueries({
        queryKey: [CAMPAIGN_CONTENT_SUBMISSIONS_QUERY_KEY, campaignId],
      });
    };
  }, [activeFiltersKey, campaignId, queryClient]);

  return (
    <>
      {actionComponent}
      <div className={styles.tableWrapper}>
        <Table
          data={contentSubmissions}
          columns={columns}
          isLoadingInitial={isLoadingInitial && !contentSubmissions.length}
          isLoading={isLoadingMore}
          showProgressBars={!!isLoadingMore}
          visibleColumns={{}}
          rowSelection={{}}
          sorting={sorting}
          onSorting={onSortingChange}
          enableSorting
          manualSorting
          enableVirtualization
          tableScrollTopKey={`content-submissions-table-scroll-${activeFiltersKey}`}
          onRowClick={handleRowClick}
          ignorePortalRowClicks
          isRowTransitioned={(row) => row.previousAasmState != null}
          onEndReached={onEndReached}
          renderBottomContent={() => (
            <InfiniteLoader
              isLoading={!!isLoadingMore && !isLoadingInitial}
              containerClassName={styles.bottomLoader}
            />
          )}
          emptyStateComponent={
            <EmptyState {...getEmptyStateContent(selectedPhase)} />
          }
          classes={{container: styles.tableContainer}}
        />
      </div>
    </>
  );
}

export default ContentSubmissionsTable;
